import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  CardHeader,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Link,
  Paper,
  Typography
} from '@material-ui/core'
import { Field, Form, Formik } from 'formik'
import {
  ChevronDown,
  History,
  Newspaper,
  NotebookTabs,
  SquareStack,
  UserRound,
  X
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { parsePhoneNumber } from 'react-phone-number-input'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import toastError from '../../errors/toastError'
import { getInitials } from '../../helpers/getInitials'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import ContactAvatarModal from '../ContactAvatarModal'
import ContactCustomFields from '../ContactCustomFields'
import { ContactNotes } from '../ContactNotes'
import ContactAuditLogs from '../Contacts/ContactAuditLogs'
import { InputField } from '../InputField'
import { InputSwitch } from '../InputSwitch'
import QueueSelectSingle from '../QueueSelectSingle'
import TicketInfoDrawer from '../TicketInfoDrawer'
import { useStyles } from './style'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'
import ContactAuditLogsModal from '../Contacts/ContactAuditLogs'
import { ButtonPrimary } from '../ButtonPrimary'

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  number: Yup.string().min(8, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email').nullable()
})

const ContactDrawer = ({
  contact,
  ticket,
  loading,
  open,
  handleDrawerClose
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState('panel1')
  const [openAvatarModal, setOpenAvatarModal] = useState(false)
  const [openAuditModal, setOpenAuditModal] = useState(false)

  const initialState = {
    name: '',
    number: '',
    email: '',
    disableBot: false,
    blacklist: false,
    silencioso: false,
    defaultQueueId: '',
    countryCode: ''
  }
  const [contactActive, setContactActive] = useState(initialState)

  useEffect(() => {
    if (contact) {
      const formattedNumber = contact.number?.startsWith('+')
        ? contact.number
        : `+${contact.number}`
      setContactActive({
        name: contact.name || '',
        number: formattedNumber,
        email: contact.email || '',
        site: contact.site || '',
        company: contact.company || '',
        disableBot: contact.disableBot || false,
        blacklist: contact.blacklist || false,
        silencioso: contact.silencioso || false,
        defaultQueueId:
          contact.defaultQueueId || contact.defaultQueue?.id || '',
        countryCode: contact.countryCode || ''
      })
    }
  }, [contact])

  useEffect(() => {
    setExpanded(ticket?.isGroup ? 'panel2' : 'panel1')
  }, [ticket?.id, ticket?.isGroup])

  const handleAccordionChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handleSaveContact = async values => {
    try {
      if (contact?.id) {
        const payload = { ...values }

        if (payload.number) {
          payload.number = payload.number.replace(/\D/g, '')
        }

        if (payload.defaultQueueId === '') {
          payload.defaultQueueId = null
        }
        if (payload.email == null) {
          payload.email = ''
        }

        await api.put(`/contacts/${contact.id}`, payload)
        toast.success('Contato salvo com sucesso!')
      }
    } catch (err) {
      toastError(err)
    }
  }

  const handleCloseOrOpenAvatar = () => {
    setOpenAvatarModal(false)
  }

  const handleAvatarUpdate = newAvatarUrl => {
    if (contact) {
      setContactActive(prev => ({ ...prev, profilePicUrl: newAvatarUrl }))
    }
  }

  const handleSwitchChange = async (
    fieldName,
    fieldValue,
    values,
    setFieldValue
  ) => {
    setFieldValue(fieldName, fieldValue)

    const updatedValues = { ...values, [fieldName]: fieldValue }
    const payload = { ...updatedValues }

    if (payload.number) {
      payload.number = payload.number.replace(/\D/g, '')
    }
    if (payload.defaultQueueId === '') {
      payload.defaultQueueId = null
    }
    if (payload.email == null) {
      payload.email = ''
    }

    try {
      if (contact?.id) {
        await api.put(`/contacts/${contact.id}`, payload)
        toast.success('Configuração do contato atualizada!')
      }
    } catch (err) {
      toastError(err)
      setFieldValue(fieldName, !fieldValue)
    }
  }

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`)
    } catch (err) {
      toastError(err)
    }
  }

  const linkedGroupUsers = Array.isArray(ticket?.users) ? ticket.users : []

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{
        style: { position: 'absolute' }
      }}
      BackdropProps={{ style: { position: 'absolute' } }}
      ModalProps={{
        container: document.getElementById('drawer-container'),
        style: { position: 'absolute' }
      }}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div className={classes.container}>
        {loading ? (
          <CircularProgress />
        ) : (
          <div className={classes.content}>
            <Formik
              initialValues={contactActive}
              enableReinitialize={true}
              validationSchema={ContactSchema}
              onSubmit={(values, actions) => {
                handleSaveContact(values)
                actions.setSubmitting(false)
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur: formikHandleBlur,
                setFieldValue
              }) => {
                let isoCode = ''
                if (values.number) {
                  try {
                    const numberToParse = values.number.startsWith('+')
                      ? values.number
                      : `+${values.number}`

                    const phoneNumber = parsePhoneNumber(numberToParse)
                    isoCode = phoneNumber?.country || ''
                  } catch (err) {
                    toast.error(err)
                  }
                }
                return (
                  <>
                    <Paper
                      square
                      elevation={0}
                      className={classes.contactHeader}
                    >
                      <div className={classes.closeButton}>
                        <IconButton onClick={handleDrawerClose}>
                          <X size={18} />
                        </IconButton>
                      </div>
                      <CardHeader
                        className={classes.cardHeader}
                        avatar={
                          <Avatar
                            onClick={() => setOpenAvatarModal(true)}
                            className={classes.contactAvatar}
                            src={contact?.profilePicUrl}
                            alt="contact_image"
                          >
                            {getInitials(contact?.name)}
                          </Avatar>
                        }
                        title={
                          <Typography className={classes.contactName}>
                            {contact?.name}
                          </Typography>
                        }
                        subheader={
                          <div className={classes.containerContactPhone}>
                            {isoCode && (
                              <img
                                src={`https://flagcdn.com/24x18/${isoCode.toLowerCase()}.png`}
                                alt={`Bandeira ${isoCode}`}
                                width="20"
                                height="14"
                              />
                            )}
                            <Typography className={classes.contactNumber}>
                              <Link
                                href={`tel:${values.number || contact?.number}`}
                              >
                                {values.number || contact?.number}
                              </Link>
                            </Typography>
                          </div>
                        }
                      />
                    </Paper>
                    <Form>
                      {!ticket.isGroup && (
                        <Accordion
                          onChange={handleAccordionChange('panel1')}
                          className={classes.accordion}
                          elevation={0}
                        >
                          <AccordionSummary
                            expandIcon={<ChevronDown />}
                            className={classes.accordionSummary}
                          >
                            <Typography
                              variant="subtitle1"
                              className={classes.accordionTitle}
                            >
                              <UserRound size={20} />
                              Informações
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails
                            className={classes.accordionDetails}
                          >
                            <Grid container spacing={1}>
                              <Grid
                                item
                                xs={12}
                                onBlurCapture={e => {
                                  if (e.target.name === 'name') {
                                    formikHandleBlur(e)
                                    handleSaveContact({
                                      ...values,
                                      name: e.target.value
                                    })
                                  }
                                }}
                              >
                                <InputField
                                  name="name"
                                  id="name"
                                  label={i18n.t('contactModal.form.name')}
                                  fullWidth
                                  value={values.name}
                                  onChange={e => handleChange(e)}
                                  error={touched.name && Boolean(errors.name)}
                                  helperText={touched.name && errors.name}
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                onBlurCapture={e => {
                                  if (e.target.name === 'email') {
                                    formikHandleBlur(e)
                                    handleSaveContact({
                                      ...values,
                                      email: e.target.value
                                    })
                                  }
                                }}
                              >
                                <InputField
                                  type="email"
                                  name="email"
                                  label="E-mail"
                                  fullWidth
                                  value={values.email}
                                  onChange={e => handleChange(e)}
                                  error={touched.email && Boolean(errors.email)}
                                  helperText={touched.email && errors.email}
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                onBlurCapture={e => {
                                  if (e.target.name === 'site') {
                                    formikHandleBlur(e)
                                    handleSaveContact({
                                      ...values,
                                      site: e.target.value
                                    })
                                  }
                                }}
                              >
                                <InputField
                                  type="text"
                                  name="site"
                                  label="Site"
                                  fullWidth
                                  value={values.site}
                                  onChange={e => handleChange(e)}
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                onBlurCapture={e => {
                                  if (e.target.name === 'company') {
                                    formikHandleBlur(e)
                                    handleSaveContact({
                                      ...values,
                                      company: e.target.value
                                    })
                                  }
                                }}
                              >
                                <InputField
                                  type="text"
                                  name="company"
                                  label="Empresa"
                                  fullWidth
                                  value={values.company}
                                  onChange={e => handleChange(e)}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Field name="defaultQueueId">
                                  {({ field, form }) => (
                                    <QueueDepartmentSelect
                                      label={i18n.t(
                                        'contactModal.form.defaultQueue'
                                      )}
                                      placeholder="Selecione o departamento padrão"
                                      fetchAllQueues={true}
                                      showLeafQueuesOnly={true}
                                      includeAllOption={false}
                                      filterNotificationAct={true}
                                      showBots={false}
                                      isMulti={false}
                                      allowEmpty={true}
                                      disabled={loading}
                                      value={
                                        field.value ? [{ id: field.value }] : []
                                      }
                                      onChange={selectedItems => {
                                        const selectedObj =
                                          selectedItems.length > 0
                                            ? selectedItems[0]
                                            : null
                                        const nextValue = selectedObj
                                          ? selectedObj.id
                                          : ''
                                        form.setFieldValue(
                                          'defaultQueueId',
                                          nextValue
                                        )
                                        const updatedValues = {
                                          ...values,
                                          defaultQueueId:
                                            nextValue === '' ? null : nextValue
                                        }
                                        handleSaveContact(updatedValues)
                                      }}
                                    />
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={12}>
                                <InputSwitch
                                  label="Bloquear Contato"
                                  size="small"
                                  checked={values.blacklist}
                                  onChange={e => {
                                    const isChecked = e?.target
                                      ? e.target.checked
                                      : Boolean(e)
                                    handleSwitchChange(
                                      'blacklist',
                                      isChecked,
                                      values,
                                      setFieldValue
                                    )
                                  }}
                                  name="blacklist"
                                  color="primary"
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <InputSwitch
                                  label={'Bot'}
                                  size="small"
                                  checked={values.disableBot}
                                  onChange={e => {
                                    const isChecked = e?.target
                                      ? e.target.checked
                                      : Boolean(e)
                                    handleSwitchChange(
                                      'disableBot',
                                      isChecked,
                                      values,
                                      setFieldValue
                                    )
                                  }}
                                  name="disableBot"
                                  color="primary"
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <InputSwitch
                                  label={'Finalização Silenciosa'}
                                  size="small"
                                  checked={values.silencioso}
                                  onChange={e => {
                                    const isChecked = e?.target
                                      ? e.target.checked
                                      : Boolean(e)
                                    handleSwitchChange(
                                      'silencioso',
                                      isChecked,
                                      values,
                                      setFieldValue
                                    )
                                  }}
                                  name="silencioso"
                                  color="primary"
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      <Accordion
                        expanded={expanded === 'panel3'}
                        onChange={handleAccordionChange('panel3')}
                        className={classes.accordion}
                        elevation={0}
                      >
                        <AccordionSummary
                          expandIcon={<ChevronDown />}
                          className={classes.accordionSummary}
                        >
                          <Typography
                            variant="subtitle1"
                            className={classes.accordionTitle}
                          >
                            <NotebookTabs size={20} />
                            Adicionais
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                          <ContactCustomFields contact={contact} />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion
                        expanded={expanded === 'panel4'}
                        onChange={handleAccordionChange('panel4')}
                        className={classes.accordion}
                        elevation={0}
                      >
                        <AccordionSummary
                          expandIcon={<ChevronDown />}
                          className={classes.accordionSummary}
                        >
                          <Typography
                            variant="subtitle1"
                            className={classes.accordionTitle}
                          >
                            <Newspaper size={20} />
                            Observações
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                          <ContactNotes ticket={ticket} />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion
                        expanded={expanded === 'panel5'}
                        onChange={handleAccordionChange('panel5')}
                        className={classes.accordion}
                        elevation={0}
                      >
                        <AccordionSummary
                          expandIcon={<ChevronDown />}
                          className={classes.accordionSummary}
                        >
                          <Typography
                            variant="subtitle1"
                            className={classes.accordionTitle}
                          >
                            <SquareStack size={20} />
                            Histórico
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                          <TicketInfoDrawer
                            contact={contact}
                            ticket={ticket}
                            open={true} // Mantemos true pois o controle de visibilidade é do Accordion
                            loading={loading}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <ButtonPrimary
                        startIcon={<History size={20} />}
                        onClick={() => setOpenAuditModal(true)}
                        disabled={!contact?.id}
                        label="Ver Auditoria"
                        style={{ marginTop: '16px' }}
                      />
                    </Form>
                  </>
                )
              }}
            </Formik>
            {ticket.isGroup && (
              <Button
                className={classes.buttonRemove}
                variant="contained"
                onClick={handleDeleteTicket}
              >
                Excluir Grupo
              </Button>
            )}
          </div>
        )}
      </div>

      <ContactAuditLogsModal
        open={openAuditModal}
        onClose={() => setOpenAuditModal(false)}
        contactId={contact?.id}
      />

      <ContactAvatarModal
        open={openAvatarModal}
        onClose={handleCloseOrOpenAvatar}
        avatarUrl={contact?.profilePicUrl}
        alt={`Imagem de ${contact?.name}`}
        contactId={contact?.id}
        onAvatarUpdate={handleAvatarUpdate}
      />
    </Drawer>
  )
}

export default ContactDrawer
